import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { ROUTES } from '@/constants/routes';
import { useUser, useSetUserActive, useSetUserCompanyAdmin } from '@/hooks/useBackofficeData';
import styles from './UserDetailPage.module.scss';

const ROLE_LABELS: Record<string, string> = {
  student: 'Estudiante',
  company_admin: 'Admin empresa',
  backoffice: 'Backoffice',
};

const PLATFORM_LABELS: Record<string, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
};

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { user, isLoading } = useUser(isNew ? undefined : id);
  const { setUserActive, isSettingActive } = useSetUserActive();
  const { setUserCompanyAdmin, isSettingCompanyAdmin } = useSetUserCompanyAdmin();
  const [active, setActive] = useState<boolean | null>(null);
  const [companyAdmin, setCompanyAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (user) {
      setActive(user.isActive);
      setCompanyAdmin(user.isCompanyAdmin ?? false);
    }
  }, [user]);

  // Alta directa: no aplica (invite-only). Los usuarios se crean por invitación.
  if (isNew) {
    return (
      <div className={styles.userDetail}>
        <div className={styles.userDetail__header}>
          <IconButton
            icon={ArrowLeft}
            onClick={() => navigate(ROUTES.BACKOFFICE.USERS)}
            aria-label="Volver a Usuarios"
          />
        </div>
        <h1>Crear usuario</h1>
        <Card>
          <CardContent>
            <p className={styles.userDetail__placeholder}>
              Los usuarios se dan de alta por invitación. Usa el botón «Crear usuario» del listado
              para invitar por email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.userDetail}>
        <h1>Cargando...</h1>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.userDetail}>
        <div className={styles.userDetail__header}>
          <IconButton
            icon={ArrowLeft}
            onClick={() => navigate(ROUTES.BACKOFFICE.USERS)}
            aria-label="Volver a Usuarios"
          />
        </div>
        <h1>Usuario no encontrado</h1>
      </div>
    );
  }

  const isActive = active ?? user.isActive;
  const isCompanyAdmin = companyAdmin ?? user.isCompanyAdmin ?? false;
  // La capacidad aditiva "también admin de empresa" solo aplica a alumnos con empresa.
  const canGrantCompanyAdmin = user.role === 'student' && !!user.companyId;

  const handleToggle = async () => {
    if (!id) return;
    const updated = await setUserActive(id, !isActive);
    if (updated) setActive(updated.isActive);
  };

  const handleToggleCompanyAdmin = async () => {
    if (!id) return;
    const updated = await setUserCompanyAdmin(id, !isCompanyAdmin);
    if (updated) setCompanyAdmin(updated.isCompanyAdmin ?? false);
  };

  const rows: Array<{ label: string; value: ReactNode }> = [
    { label: 'Email', value: user.email },
    { label: 'Rol', value: ROLE_LABELS[user.role] ?? user.role },
    { label: 'Empresa', value: user.companyName || '—' },
    { label: 'Nivel', value: user.level || '—' },
    {
      label: 'Registro',
      value: user.registeredAt ? new Date(user.registeredAt).toLocaleDateString() : '—',
    },
  ];

  return (
    <div className={styles.userDetail}>
      <div className={styles.userDetail__header}>
        <IconButton
          icon={ArrowLeft}
          onClick={() => navigate(ROUTES.BACKOFFICE.USERS)}
          aria-label="Volver a Usuarios"
        />
        <Button
          variant={isActive ? 'destructive' : 'primary'}
          onClick={handleToggle}
          isLoading={isSettingActive}
        >
          {isActive ? 'Desactivar' : 'Activar'}
        </Button>
      </div>

      <div className={styles.userDetail__titleRow}>
        <h1>{user.name}</h1>
        <Badge variant={isActive ? 'success' : 'error'}>{isActive ? 'Activo' : 'Inactivo'}</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del usuario</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className={styles.userDetail__list}>
            {rows.map((r) => (
              <div key={r.label} className={styles.userDetail__row}>
                <dt className={styles.userDetail__label}>{r.label}</dt>
                <dd className={styles.userDetail__value}>{r.value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {user.devices && user.devices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Dispositivos</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className={styles.userDetail__list}>
              {user.devices.map((d, i) => (
                <div key={`${d.platform}-${i}`} className={styles.userDetail__row}>
                  <dt className={styles.userDetail__label}>
                    {PLATFORM_LABELS[d.platform] ?? d.platform}
                  </dt>
                  <dd className={styles.userDetail__value}>
                    {d.appVersion ? `v${d.appVersion}` : 'sin versión'} · visto{' '}
                    {new Date(d.lastSeenAt).toLocaleDateString()}
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      {canGrantCompanyAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Admin de empresa</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={styles.userDetail__cardText}>
              {isCompanyAdmin
                ? 'Este alumno también puede administrar su empresa. En escritorio entra al panel de empresa; en la app o el móvil, a su experiencia de aprendizaje.'
                : 'Concede a este alumno acceso de administración de su empresa, además de su experiencia de aprendizaje.'}
            </p>
            <Button
              variant={isCompanyAdmin ? 'destructive' : 'primary'}
              onClick={handleToggleCompanyAdmin}
              isLoading={isSettingCompanyAdmin}
            >
              {isCompanyAdmin ? 'Quitar admin de empresa' : 'Hacer admin de empresa'}
            </Button>
          </CardContent>
        </Card>
      )}

      {!isActive && (
        <p className={styles.userDetail__note}>
          Esta cuenta está desactivada: el usuario no puede iniciar sesión.
        </p>
      )}
    </div>
  );
}

export default UserDetailPage;
